import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';

import { IdentitySeason } from '@reg/shared/model';
import { HttpServiceHelper, ToastService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service for season data.
 */
@Injectable()
export class IdentitySeasonService {
    public static INCLUDE_ORGANIZATION: string = 'organization';
    public static INCLUDE_SPORT: string = 'sport';

    private identityApiBaseUrl: string;
    private cachedSeasonsMap: { [apiRequest: string]: IdentitySeason[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper, private toastService: ToastService) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    public clearCache(): void {
        this.cachedSeasonsMap = {};
    }

    /**
     * Get a seasons
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentitySeason[]> {
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh && this.cachedSeasonsMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedSeasonsMap[apiRequestUrl].map((a: IdentitySeason) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const seasons: IdentitySeason[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const season of bodyData) {
                    seasons.push((new IdentitySeason()).deserialize(season, included));
                }

                this.cachedSeasonsMap[apiRequestUrl] = seasons;
                return seasons.map((season: IdentitySeason) => season.clone());
            }));
    }

    /**
     * Get a single season
     */
    public get(
        identitySeasonId: string,
        includes: string[] = null,
        queryParams: string[] = [],
    ): Observable<IdentitySeason> {
        const apiRequestUrl: string = this.getApiUrl(identitySeasonId, includes, queryParams);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentitySeason().deserialize(response))));
    }

    /**
     * Add or update a season
     */
    public save(
        season: IdentitySeason,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentitySeason> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(season.id, includes, queryParams);

        if (season.id) {
            // Update existing Season
            response = this.http.patch(apiRequestUrl, season.serialize());
        } else {
            // Add a new Season
            response = this.http.post(apiRequestUrl, season.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentitySeason()).deserialize(response)),
                tap(() => this.toastService.showSuccess('Successfully saved season')),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified season.
     */
    public delete(identitySeasonId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identitySeasonId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Calculate rankings
     */
    public calculateRankings(identitySeasonId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(identitySeasonId)}/calculate-rankings`;
        this.http.spinnerService.show(600, 'Updating Rankings...');

        return this.http.patch(apiRequestUrl, null, false, false)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.http.spinnerService.hide(true)),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 409) {
                        let msg: string = HttpServiceHelper.getErrorMessage(error);

                        if (error.error?.errors?.length > 0 &&
                            error.error?.errors[0]?.source?.length) {
                            msg += ` ${error.error.errors[0].source.join('<br/>')}`;
                        }

                        this.http.toastService.showError(msg);
                    } else {
                        return this.http.handleError(error, this.http.toastService);
                    }
                }),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(assessorId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/seasons/`;

        if (assessorId) {
            url += assessorId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
